/**
 * Dashboard (§13.3), in the Titanium layout (Phase 8).
 *
 * A sentence first — what needs doing, in words — then the deck, the money
 * tiles, the runway and the card wall.
 *
 * The rule this page follows: **a number appears only when it traces to
 * evidence, and it looks like the evidence it came from.** Statement-backed
 * figures are solid. "Spend this cycle" traces to transaction *alerts*, which
 * banks send inconsistently — so it gets the provisional treatment (§2.7):
 * dashed, blue rather than gold, and copy calling it a floor rather than a total.
 *
 * When there is no evidence at all, a tile says so instead of showing ₹0. A
 * zero on a money dashboard is a claim, not a placeholder.
 *
 * The headline leads with what is *due*, not with "you owe": the outstanding
 * total includes bills past their due date that are almost certainly paid but
 * can't be confirmed until the next statement lands (see `unconfirmed`), so
 * "you owe ₹1.5 lakh" would be the dashboard overstating the user's debt.
 */

import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, ArrowUpRight, Calculator, CreditCard, Plus } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { CardObject, CardObjectSkeleton } from "@/components/cards/CardObject";
import { DueRunway, urgencyLabel } from "@/components/dashboard/DueRunway";
import { NextUp } from "@/components/dashboard/NextUp";
import { summaryToCard } from "@/components/dashboard/summaryToCard";
import { MonthEndTile } from "@/components/dashboard/MonthEndTile";
import { WalletFan } from "@/components/dashboard/WalletFan";
import { Odometer, PulseDot, stagger, useTilt } from "@/components/ui/motion";
import { Button, CardSwatch, EmptyState, Panel, SectionTitle } from "@/components/ui/primitives";
import {
  cashbackApi,
  dashboardApi,
  type RunwayEntry,
  type CardSummary,
  type DashboardSummary,
  type LiveFigure,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { useCommands } from "@/lib/commands";
import {
  dayParts,
  daysUntil,
  formatDate,
  formatFiscalYear,
  formatMoney,
  formatMoneyCompact,
  formatMonth,
  formatMonthShort,
  numberWord,
  weekdayName,
} from "@/lib/format";

export function DashboardPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["dashboard"],
    queryFn: dashboardApi.summary,
  });

  if (isError) {
    return (
      <Panel className="mx-auto max-w-2xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-overdue" aria-hidden />
          <div className="flex-1">
            <p className="text-sm text-ink">Couldn't load the dashboard.</p>
            <p className="mt-0.5 text-xs text-muted">{(error as Error).message}</p>
          </div>
          <Button variant="default" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </Panel>
    );
  }

  if (isLoading || !data) return <DashboardSkeleton />;

  if (data.cards.length === 0) return <FirstRun />;

  return (
    <div className="space-y-10">
      <Lede data={data} />

      <section className="grid grid-cols-[minmax(0,1fr)] gap-3.5 lg:grid-cols-12">
        <Panel className="animate-rise overflow-hidden p-5 sm:p-6 lg:col-span-7 lg:row-span-3 max-md:order-5" style={stagger(0, 120)}>
          <WalletFan cards={data.cards} today={data.today} />
        </Panel>
        <CashbackTile data={data} />
        <OutstandingTile data={data} />
        <MonthEndTile data={data.due_by_month_end} today={data.today} className="lg:col-span-5 max-md:order-2" />
        <NextBills entries={data.runway} today={data.today} />

        <LiveTile figure={data.cycle_spend} />
        <div className="animate-rise lg:col-span-4 max-md:order-4" style={stagger(4, 120)}>
          <NextUp events={data.cycle_events} limit={4} />
        </div>
        <ByCardTile fiscalYear={data.fiscal_year} />
      </section>

      <section className="animate-rise" style={stagger(6, 120)}>
        <DueRunway
          entries={data.runway}
          events={data.cycle_events}
          days={data.runway_days}
          today={data.today}
        />
      </section>

      <section className="space-y-5">
        <SectionTitle
          action={
            <Link
              to="/cards"
              className="inline-flex items-center gap-1 text-sm text-muted transition-colors hover:text-ink"
            >
              Manage <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          }
        >
          All cards
        </SectionTitle>
        <div className="grid gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data.cards.map((summary, i) => (
            <WallCard key={summary.card_id} summary={summary} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}

// --- The headline ------------------------------------------------------------

function Lede({ data }: { data: DashboardSummary }) {
  const { openPlanner } = useCommands();

  return (
    <section className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0 animate-rise">
        <div className="label-micro">
          {weekdayName(data.today)} · {formatDate(data.today)} · {formatFiscalYear(data.fiscal_year)}
        </div>
        <h1 className="mt-3 max-w-[48rem] text-headline font-medium text-ink">
          <Headline data={data} />
        </h1>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2 animate-rise" style={stagger(1, 60)}>
        {data.needs_review > 0 && (
          <Button asChild variant="default" size="lg">
            <Link to="/statements">
              <AlertTriangle className="h-4 w-4 text-warn" aria-hidden />
              Review {data.needs_review} statement{data.needs_review === 1 ? "" : "s"}
            </Link>
          </Button>
        )}
        {/* Phones have "Which card?" in the middle of the tab bar instead. */}
        <Button variant="primary" size="lg" onClick={openPlanner} className="max-md:hidden">
          <Calculator className="h-4 w-4" aria-hidden />
          Plan a purchase
        </Button>
      </div>
    </section>
  );
}

function Headline({ data }: { data: DashboardSummary }) {
  const soft = (text: string) => <span className="text-muted">{text}</span>;
  const em = (text: string) => <em className="accent-serif pr-[0.06em]">{text}</em>;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  if (data.overdue_count > 0) {
    return (
      <>
        {cap(numberWord(data.overdue_count))} bill{data.overdue_count === 1 ? " is" : "s are"}{" "}
        <em className="font-serif font-normal italic text-overdue">overdue</em> —{" "}
        {soft(formatMoneyCompact(data.overdue_total))} still unpaid.
      </>
    );
  }

  if (data.due_next_7_days_count > 0) {
    // The soonest day with a bill on it, and how many bills share it.
    const upcoming = data.runway.filter(
      (e) => e.day_offset >= 0 && e.day_offset <= 7 && e.urgency !== "paid",
    );
    const soonest = Math.min(...upcoming.map((e) => e.day_offset));
    const onSoonest = upcoming.filter((e) => e.day_offset === soonest).length;
    const when = soonest === 0 ? "today" : soonest === 1 ? "tomorrow" : `in ${soonest} days`;
    return (
      <>
        {soft(formatMoneyCompact(data.due_next_7_days))} falls due this week.{" "}
        {Number.isFinite(soonest) && onSoonest > 0 && (
          <>
            {em(cap(numberWord(onSoonest)))} {onSoonest === 1 ? "bill is" : "are"} due {when}.
          </>
        )}
      </>
    );
  }

  if (data.outstanding_card_count > 0) {
    return (
      <>
        {em("Nothing")} is due this week. {soft(formatMoneyCompact(data.total_outstanding))}{" "}
        on {numberWord(data.outstanding_card_count)} statement
        {data.outstanding_card_count === 1 ? "" : "s"} is already in hand.
      </>
    );
  }

  return (
    <>
      {em("All clear.")} No bill is waiting on you across {numberWord(data.cards.length)} card
      {data.cards.length === 1 ? "" : "s"}.
    </>
  );
}

// --- Money tiles -------------------------------------------------------------

/**
 * Phones only (mobile M3): the next bills as a row of chips you swipe through —
 * card, printed due date, printed amount. Statement-backed, so gold; a bill
 * past its date with no payment seen yet is left out (it is "awaiting
 * confirmation", not next), as on the month-end figure.
 */
function NextBills({ entries, today }: { entries: RunwayEntry[]; today: string }) {
  const next = entries
    .filter((e) => e.urgency !== "paid" && e.urgency !== "unconfirmed" && e.due_date >= today)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  if (next.length === 0) return null;
  return (
    <section className="max-md:order-3 md:hidden" aria-label="Next bills">
      <div className="label-micro mb-2 px-1">Next bills</div>
      <div className="-mx-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
        {next.map((e) => (
          <Link
            key={e.statement_id}
            to={`/cards/${e.card_id}`}
            className="flex min-h-[4.5rem] w-[11.5rem] shrink-0 snap-start flex-col justify-between rounded-2xl border border-hairline bg-panel px-3.5 py-3 active:bg-raised"
          >
            <span className="flex items-center gap-2 text-xs text-muted">
              <CardSwatch theme={e.theme} />
              <span className="truncate">{e.nickname ?? `•••• ${e.last4}`}</span>
            </span>
            <span className="mt-2 flex items-baseline justify-between gap-2">
              <span className={cn("whitespace-nowrap text-sm", e.urgency === "overdue" ? "text-overdue" : "text-ink")}>
                {dayParts(e.due_date).day} {dayParts(e.due_date).month}
              </span>
              <span className="font-medium tabular-nums text-brass">{formatMoney(e.amount)}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function CashbackTile({ data }: { data: DashboardSummary }) {
  const { data: monthly } = useQuery({
    queryKey: ["cashback", "monthly", "fiscal_year", data.fiscal_year, null],
    queryFn: () => cashbackApi.monthly("fiscal_year", data.fiscal_year),
  });

  const thisMonth = data.today.slice(0, 7);
  const months = monthly?.months ?? [];
  const values = months.map((m) => Math.max(0, Number(m.real_cashback)));
  const max = Math.max(1, ...values);
  const last = [...months].reverse().find((m) => m.month < thisMonth);

  return (
    <Panel className="panel-gold animate-rise p-5 sm:p-6 lg:col-span-5 max-md:order-1" style={stagger(1, 120)}>
      <div className="flex items-center justify-between gap-2">
        <span className="label-micro">Real cashback · {formatFiscalYear(data.fiscal_year)}</span>
        <Link to="/cashback" className="text-faint transition-colors hover:text-ink" aria-label="Open cashback">
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-4 text-figure-lg font-normal text-brass">
        <Odometer value={formatMoney(data.real_cashback_fy)} delay={0.35} minorClassName="text-[0.5em] text-brass-dim" />
      </div>
      <p className="mt-2 text-[13px] text-muted">
        Earned by spending, net of clawbacks.
        {last && Number(last.real_cashback) > 0 && (
          <>
            {" "}
            <span className="text-brass">+{formatMoney(last.real_cashback)}</span> in{" "}
            {formatMonth(last.month).split(" ")[0]}.
          </>
        )}
      </p>

      {months.length > 0 && (
        <div className="mt-5 flex h-[4.5rem] items-end gap-1.5" aria-hidden>
          {months.map((m, i) => {
            const partial = m.month === thisMonth;
            const v = Math.max(0, Number(m.real_cashback));
            return (
              <div key={m.month} className="flex flex-1 flex-col items-center gap-1.5" title={`${formatMonth(m.month)}: ${formatMoney(m.real_cashback)}${partial ? " (month in progress)" : ""}`}>
                <div className="flex h-12 w-full items-end">
                  <div
                    className={cn(
                      "w-full origin-bottom animate-grow-y rounded-t-[5px] rounded-b-[2px]",
                      partial
                        ? "border border-dashed border-brass/50 bg-[repeating-linear-gradient(135deg,rgb(var(--brass)/0.3)_0_3px,transparent_3px_7px)]"
                        : "bg-gradient-to-b from-brass/75 to-brass/15",
                    )}
                    style={{
                      height: `${Math.max(6, (v / max) * 100)}%`,
                      animationDelay: `${500 + i * 55}ms`,
                    }}
                  />
                </div>
                <span className="font-mono text-[9.5px] text-faint">
                  {formatMonthShort(m.month).charAt(0)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function OutstandingTile({ data }: { data: DashboardSummary }) {
  const owing = data.cards
    .filter((c) => c.outstanding !== null && Number(c.outstanding) > 0 && c.urgency !== "paid")
    .sort((a, b) => Number(b.outstanding) - Number(a.outstanding));
  const total = owing.reduce((a, c) => a + Number(c.outstanding), 0);

  return (
    <Panel className="animate-rise p-5 sm:p-6 lg:col-span-5 max-md:order-6" style={stagger(2, 120)}>
      <span className="label-micro">Total outstanding</span>
      <div className="mt-4 text-figure-lg font-light text-ink">
        <Odometer value={formatMoney(data.total_outstanding)} delay={0.45} />
      </div>
      <p className="mt-1.5 text-[13px] text-muted">
        Across {numberWord(data.outstanding_card_count)} card
        {data.outstanding_card_count === 1 ? "" : "s"}, from the latest statement of each.
      </p>

      <div className="mt-5 flex items-center gap-5">
        {owing.length > 0 && <Ring cards={owing} total={total} />}
        <dl className="grid min-w-0 flex-1 grid-cols-2 gap-3">
          <MiniFigure
            label="Due in 7 days"
            value={formatMoney(data.due_next_7_days)}
            note={
              data.due_next_7_days_count === 0
                ? "nothing this week"
                : `${data.due_next_7_days_count} bill${data.due_next_7_days_count === 1 ? "" : "s"}`
            }
            tone={data.due_next_7_days_count > 0 ? "text-warn" : undefined}
          />
          {data.overdue_count > 0 ? (
            <MiniFigure
              label="Overdue"
              value={formatMoney(data.overdue_total)}
              note={`${data.overdue_count} unpaid`}
              tone="text-overdue"
            />
          ) : (
            <MiniFigure
              label="Awaiting confirmation"
              value={formatMoney(data.unconfirmed_total)}
              note={
                data.unconfirmed_count === 0
                  ? "nothing pending"
                  : `${data.unconfirmed_count} bill${data.unconfirmed_count === 1 ? "" : "s"} past due`
              }
              hint="These bills are past their due date, but a payment only appears on the next statement — so MyDeck can't confirm them yet. It is not saying they're unpaid."
            />
          )}
        </dl>
      </div>
    </Panel>
  );
}

function Ring({ cards, total }: { cards: CardSummary[]; total: number }) {
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setDrawn(true)));
    return () => cancelAnimationFrame(id);
  }, []);
  const R = 38;
  const L = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg viewBox="0 0 100 100" className="h-[5.5rem] w-[5.5rem] shrink-0 -rotate-90" aria-hidden>
      <circle cx="50" cy="50" r={R} fill="none" stroke="rgb(var(--border-hairline))" strokeWidth="11" />
      {cards.map((c, i) => {
        const f = Number(c.outstanding) / total;
        const offset = -acc * L;
        acc += f;
        return (
          <circle
            key={c.card_id}
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke={c.theme?.gradient_to ?? "rgb(var(--border-strong))"}
            strokeWidth="11"
            strokeDasharray={`${Math.max(0, f * L - 1.2)} ${L}`}
            strokeDashoffset={offset}
            style={{
              opacity: drawn ? 1 : 0,
              transition: `opacity 0.6s ease ${0.5 + i * 0.07}s`,
            }}
          >
            <title>
              {c.nickname ?? c.issuer}: {formatMoney(c.outstanding)}
            </title>
          </circle>
        );
      })}
    </svg>
  );
}

function MiniFigure({
  label,
  value,
  note,
  tone,
  hint,
}: {
  label: string;
  value: string;
  note: string;
  tone?: string;
  hint?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-hairline bg-raised/50 p-3" title={hint}>
      <dt className="truncate text-[11.5px] text-faint">{label}</dt>
      <dd className={cn("mt-1 truncate text-[15px] font-medium tracking-[-0.02em] text-ink", tone)}>
        {value}
      </dd>
      <dd className="mt-0.5 truncate text-[11px] text-faint">{note}</dd>
    </div>
  );
}

/**
 * A figure built from alerts rather than statements (§10.4, §2.7): dashed,
 * blue, chipped, and captioned as a floor. It must never look like
 * "Total outstanding" — the banks that don't alert leave no trace at all.
 */
function LiveTile({ figure }: { figure: LiveFigure }) {
  const known = figure.value !== null;
  return (
    <Panel className="panel-live animate-rise p-5 lg:col-span-4 max-md:order-7" style={stagger(3, 120)} title={figure.caveat}>
      <div className="flex items-center gap-2">
        <PulseDot tone="live" />
        <span className="label-micro">Spend this cycle · live</span>
        <span className="provisional-chip ml-auto">Provisional</span>
      </div>
      <div className={cn("mt-4 text-figure font-light", known ? "text-ink/90" : "text-faint/50")}>
        {known ? <Odometer value={formatMoney(figure.value)} delay={0.55} /> : "—"}
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-muted">
        {known
          ? `From ${figure.txn_count} alert${figure.txn_count === 1 ? "" : "s"} across ${figure.cards_counted} card${figure.cards_counted === 1 ? "" : "s"} — a floor, not a total. Statements replace it as they land.`
          : figure.unlocks}
      </p>
    </Panel>
  );
}

/** Which cards earned the year's cashback — the effective-rate story at a glance. */
function ByCardTile({ fiscalYear }: { fiscalYear: string }) {
  const { data } = useQuery({
    queryKey: ["cashback", "summary", fiscalYear],
    queryFn: () => cashbackApi.summary(fiscalYear),
  });
  const rows = (data?.cards ?? [])
    .filter((c) => Number(c.real_cashback) > 0)
    .sort((a, b) => Number(b.real_cashback) - Number(a.real_cashback))
    .slice(0, 4);
  const max = Math.max(1, ...rows.map((r) => Number(r.real_cashback)));

  return (
    <Panel className="animate-rise p-5 lg:col-span-4 max-md:order-8" style={stagger(5, 120)}>
      <div className="flex items-center justify-between">
        <span className="label-micro">Top earners · {formatFiscalYear(fiscalYear)}</span>
        <Link to="/cashback" className="text-faint transition-colors hover:text-ink" aria-label="Open cashback">
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      {!data ? (
        <div className="mt-5 space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton h-4 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted">
          No cashback credited this year yet. It appears here as statements carry it.
        </p>
      ) : (
        <ul className="mt-4 space-y-3.5">
          {rows.map((r, i) => (
            <li key={r.card_id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5">
              <span className="truncate text-[13px] text-ink">
                {r.nickname ?? r.product_name ?? r.issuer}
                {r.effective_rate && (
                  <span className="ml-2 font-mono text-[10.5px] text-faint">
                    {r.effective_rate}%
                  </span>
                )}
              </span>
              <span className="text-[13px] font-medium tabular-nums text-brass">
                {formatMoney(r.real_cashback)}
              </span>
              <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-raised">
                <span
                  className="block h-full origin-left animate-grow-x rounded-full"
                  style={{
                    width: `${(Number(r.real_cashback) / max) * 100}%`,
                    backgroundImage: r.theme
                      ? `linear-gradient(90deg, ${r.theme.gradient_from}, ${r.theme.gradient_to})`
                      : undefined,
                    animationDelay: `${600 + i * 80}ms`,
                  }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// --- Card wall ---------------------------------------------------------------

function WallCard({ summary, index }: { summary: CardSummary; index: number }) {
  const tilt = useTilt<HTMLAnchorElement>();
  const days = summary.due_date ? daysUntil(summary.due_date) : null;

  return (
    <div className="animate-rise space-y-3" style={stagger(index, 300, 50)}>
      <Link
        ref={tilt}
        to={`/cards/${summary.card_id}`}
        className="group block rounded-card [perspective:1100px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50"
      >
        <motion.div layoutId={`card-${summary.card_id}`} transition={{ type: "spring", stiffness: 260, damping: 30 }}>
          <CardObject card={summaryToCard(summary)} outstanding={summary.outstanding} />
        </motion.div>
      </Link>
      <div className="flex items-baseline justify-between gap-2 px-1">
        <span
          className={cn(
            "text-xs",
            summary.urgency === "overdue" && "text-overdue",
            (summary.urgency === "unconfirmed" || summary.urgency === "soon") && "text-warn",
            summary.urgency === "near" && "text-brass",
            summary.urgency === "later" && "text-muted",
            summary.urgency === "paid" && "text-paid",
          )}
          title={summary.payment_status_basis ?? undefined}
        >
          {summary.due_date && days !== null
            ? urgencyLabel(summary.urgency, days)
            : summary.statement_count === 0
              ? "No statements yet"
              : "No due date printed"}
        </span>
        {summary.due_date && (
          <span className="font-mono text-[11px] text-faint">{formatDate(summary.due_date)}</span>
        )}
      </div>
    </div>
  );
}

// --- Loading and first run ---------------------------------------------------

function DashboardSkeleton() {
  return (
    <div className="space-y-10" aria-busy="true" aria-label="Loading the dashboard">
      <div className="space-y-4">
        <div className="skeleton h-3 w-56" />
        <div className="skeleton h-12 w-full max-w-2xl" />
        <div className="skeleton h-12 w-2/3 max-w-xl" />
      </div>
      <div className="grid gap-3.5 lg:grid-cols-12">
        <Panel className="p-6 lg:col-span-7 lg:row-span-2">
          <div className="skeleton h-3 w-32" />
          <div className="mx-auto mt-10 w-2/3">
            <CardObjectSkeleton />
          </div>
        </Panel>
        {[0, 1].map((i) => (
          <Panel key={i} className="p-6 lg:col-span-5">
            <div className="skeleton h-3 w-40" />
            <div className="skeleton mt-5 h-12 w-56" />
            <div className="skeleton mt-6 h-14 w-full" />
          </Panel>
        ))}
      </div>
    </div>
  );
}

function FirstRun() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 pt-6">
      <div className="animate-rise">
        <div className="label-micro">Welcome</div>
        <h1 className="mt-3 text-headline font-medium text-ink">
          Every card, <em className="accent-serif">one</em> deck.
        </h1>
        <p className="mt-4 max-w-xl text-muted">
          MyDeck reads your statements and bank alerts, then shows what's due, what you've
          spent and what your cards really paid you back. Start with a card or a statement.
        </p>
      </div>
      <Panel className="animate-rise" style={stagger(1, 100)}>
        <EmptyState
          icon={CreditCard}
          title="No cards yet"
          body="Add a card, connect your inbox in Settings, or drop a statement PDF on the Statements page — MyDeck creates the card from it."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild variant="primary">
                <Link to="/cards">
                  <Plus className="h-4 w-4" />
                  Add your first card
                </Link>
              </Button>
              <Button asChild variant="default">
                <Link to="/statements">Upload a statement</Link>
              </Button>
            </div>
          }
        />
      </Panel>
    </div>
  );
}
